import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { arc, pie } from 'd3-shape';
import type { PieArcDatum } from 'd3-shape';

import { colors, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from './d3';
import { SvgChartCanvas } from './web/SvgChartCanvas.web';

export type DonutSegment = {
  /** Stable id for drill-down (e.g. category slug). */
  id?: string;
  label: string;
  value: number;
  color: string;
};

type DonutChartProps = {
  segments: DonutSegment[];
  size?: number;
  centerLabel?: string;
  centerValue?: string;
  /** When false, only the ring is rendered (parent supplies the legend). Default true. */
  showLegend?: boolean;
  /** Called when a slice or legend row is pressed. */
  onSegmentPress?: (segment: DonutSegment) => void;
};

export function DonutChart({
  segments,
  size = 140,
  centerLabel = 'This month',
  centerValue,
  showLegend = true,
  onSegmentPress,
}: DonutChartProps) {
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  const displayCenterValue = centerValue ?? formatCurrency(total);
  const outerRadius = size / 2;
  const innerRadius = outerRadius * 0.62;
  const center = outerRadius;
  const holeSize = innerRadius * 2 - 8;
  const compactCenter = displayCenterValue.length > 9;

  const arcs = useMemo(() => {
    const pieGen = pie<DonutSegment>()
      .value((d) => d.value)
      .sort(null)
      .padAngle(0.02);

    const arcGen = arc<PieArcDatum<DonutSegment>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius - 2)
      .cornerRadius(3);

    return pieGen(segments).map((datum) => ({
      key: datum.data.id ?? datum.data.label,
      color: datum.data.color,
      d: arcGen(datum) ?? '',
      percent: Math.round((datum.data.value / total) * 100),
      label: datum.data.label,
      value: datum.data.value,
      segment: datum.data,
    }));
  }, [segments, innerRadius, outerRadius, total]);

  return (
    <View style={styles.wrapper}>
      <View style={[styles.ring, { width: size, height: size }]}>
        <SvgChartCanvas width={size} height={size}>
          <g transform={`translate(${center}, ${center})`}>
            {arcs.map((slice) => (
              <path
                key={slice.key}
                d={slice.d}
                fill={slice.color}
                style={
                  onSegmentPress
                    ? { cursor: 'pointer', outline: 'none' }
                    : undefined
                }
                onClick={
                  onSegmentPress
                    ? (event) => {
                        event.stopPropagation();
                        onSegmentPress(slice.segment);
                      }
                    : undefined
                }
              />
            ))}
          </g>
        </SvgChartCanvas>
        <View
          pointerEvents="none"
          style={[
            styles.center,
            {
              width: holeSize,
              height: holeSize,
              borderRadius: holeSize / 2,
            },
          ]}
        >
          <Text style={styles.centerLabel} numberOfLines={1}>
            {centerLabel}
          </Text>
          <Text
            style={[styles.centerValue, compactCenter && styles.centerValueCompact]}
            numberOfLines={1}
          >
            {displayCenterValue}
          </Text>
        </View>
      </View>
      {showLegend && (
        <View style={styles.legend}>
          {arcs.map((slice) => {
            const body = (
              <>
                <View style={styles.legendTitleRow}>
                  <View style={[styles.dot, { backgroundColor: slice.color }]} />
                  <Text style={styles.legendLabel} numberOfLines={1}>
                    {slice.label}
                  </Text>
                </View>
                <View style={styles.legendMetaRow}>
                  <Text style={styles.legendAmount}>{formatCurrency(slice.value)}</Text>
                  <Text style={styles.legendPercent}>{slice.percent}%</Text>
                </View>
              </>
            );

            if (!onSegmentPress) {
              return (
                <View key={slice.key} style={styles.legendRow}>
                  {body}
                </View>
              );
            }

            return (
              <Pressable
                key={slice.key}
                accessibilityRole="button"
                accessibilityLabel={`${slice.label}, ${formatCurrency(slice.value)}. View transactions`}
                onPress={() => onSegmentPress(slice.segment)}
                style={({ pressed }) => [styles.legendRow, pressed && styles.legendRowPressed]}
              >
                {body}
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  ring: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flexShrink: 0,
  },
  center: {
    position: 'absolute',
    backgroundColor: colors.surfaceElevated,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    paddingHorizontal: spacing.xs,
  },
  centerLabel: {
    ...typography.label,
    color: colors.textMuted,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  centerValue: {
    ...typography.headline,
    color: colors.text,
    fontSize: 14,
    lineHeight: 18,
    fontVariant: ['tabular-nums'],
    textAlign: 'center',
    width: '100%',
  },
  centerValueCompact: {
    fontSize: 11,
    lineHeight: 14,
  },
  legend: {
    flex: 1,
    minWidth: 0,
    gap: spacing.sm,
  },
  legendRow: {
    gap: 2,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    marginHorizontal: -spacing.xs,
  },
  legendRowPressed: {
    opacity: 0.88,
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
  legendTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 0,
  },
  legendMetaRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingLeft: 16,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
  },
  legendLabel: {
    ...typography.body,
    fontSize: 14,
    lineHeight: 18,
    color: colors.text,
    flex: 1,
    minWidth: 0,
  },
  legendAmount: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
    flexShrink: 1,
  },
  legendPercent: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
    flexShrink: 0,
  },
});
